import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '@/api/client';
import { useDeleteKbCategory, useSaveKbCategory } from '@/api/kb';
import type { KbCategory } from '@/api/types';
import { Button, Input, Modal, useToast } from '@/components/ui';

const schema = z.object({
  name: z.string().trim().min(2, 'At least 2 characters').max(80),
  description: z.string().trim().max(300),
});
type FormValues = z.infer<typeof schema>;

export function CategoryModal({ category, onClose }: { category: KbCategory | null; onClose: () => void }) {
  const toast = useToast();
  const save = useSaveKbCategory();
  const remove = useDeleteKbCategory();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: category?.name ?? '', description: category?.description ?? '' },
  });

  const onSubmit = handleSubmit((input) =>
    save.mutate(
      { id: category?.id, input },
      {
        onSuccess: () => (toast.success(category ? 'Category saved' : 'Category created'), onClose()),
        onError: (e) =>
          e instanceof ApiError && e.status === 409
            ? setError('name', { message: e.message })
            : toast.error(e.message),
      },
    ),
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={category ? `Edit ${category.name}` : 'New category'}
      footer={
        <>
          {category && (
            <Button
              variant={confirmDelete ? 'danger' : 'ghost'}
              className="mr-auto"
              loading={remove.isPending}
              onClick={() =>
                confirmDelete
                  ? remove.mutate(category.id, {
                      onSuccess: () => (toast.success('Category deleted'), onClose()),
                      onError: (e) => toast.error(e.message),
                    })
                  : setConfirmDelete(true)
              }
            >
              {confirmDelete ? 'Confirm delete' : 'Delete'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="category-form" loading={save.isPending}>
            {category ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="category-form" onSubmit={onSubmit} noValidate className="space-y-4">
        <Input
          label="Name"
          required
          placeholder="Payments & payouts"
          error={errors.name?.message}
          {...register('name')}
        />
        <Input label="Description" error={errors.description?.message} {...register('description')} />
      </form>
    </Modal>
  );
}
